import { mostrarErro } from '@4beefopag/4bee-fopag-core/dist/molecules';
import { Page } from '@4beefopag/4bee-fopag-core/dist/organisms';
import { useLocalStorage } from '@4beefopag/4bee-fopag-core/dist/services/hooks';
import { LoadingOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Col, Empty, Modal, Pagination, Row, Skeleton, message, notification } from 'antd';
import fileDownload from 'js-file-download';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import usePagination from '../../../hooks/usePagination';
import {
  downloadPayrollsWorksheet,
  searchPayrolls,
  transmitToApdata,
} from '../../../services/repository/payrollRepository';
import { useQueryUrl } from '../../../services/util';
import BwgButton from '../../atoms/Button';
import ButtonPanel from '../../molecules/ButtonPanel';
import PayrollSearchFilters from '../../molecules/PayrollSearchFilters';
import DrawerEditPayroll from '../../organisms/DrawerEditPayroll';
import PayrollTable from '../../organisms/PayrollTable';
import ModalAnteciparRepasse from './ModalAnteciparRepasse';
import { getSortedItems } from '../../../services/util/sort';
import { useUserPolicy } from '../../../user-policy';

const EmptyWrapper = styled.div`
  padding: 50px;
`;

export default function Payrolls() {
  const queryParams = useQueryUrl();
  const [storedFilters, setStoredFilters] = useLocalStorage('payroll-cached-search-filters', {
    carteiras: [],
    status: [],
  });
  const { userAccount } = useUserPolicy();
  const queryClient = useQueryClient();

  const defaultFilterValues = {
    atualizadoEmInicial: moment().subtract(1, 'month').startOf('month'),
    atualizadoEmFinal: moment().endOf('month'),
    dataPagamentoCoop: {
      inicial: undefined,
      final: undefined,
    },
    dataPrevistaFaturaInicial: undefined,
    dataPrevistaFaturaFinal: undefined,
    carteiras: [],
    idsRequisicao: queryParams.get('idsRequisicao'),
    logsTransacao: queryParams.get('logsTransacao'),
    folhas: undefined,
    status: [],
  };
  const [filtros, setFiltros] = useState({ ...defaultFilterValues, ...storedFilters });
  const [showPaymentAdvanceModal, setShowPaymentAdvanceModal] = useState(false);
  const [payrollSelected, setPayrollSelected] = useState();
  const pagination = usePagination();

  const [filteredPayrolls, setFilteredPayrolls] = useState([]);
  const [payrollPage, setPayrollPage] = useState([]);
  const [editingPayroll, setEditingPayroll] = useState();
  const [orderBy, setOrderBy] = useState({});
  const [carteirasDisponiveis, setCarteirasDisponiveis] = useState([]);

  const payrollsQueryKey = [
    'payrolls',
    filtros.atualizadoEmInicial,
    filtros.atualizadoEmFinal,
    filtros.dataPrevistaFaturaInicial,
    filtros.dataPrevistaFaturaFinal,
    filtros.idsRequisicao,
    filtros.logsTransacao,
    filtros.dataPagamentoCoop,
    filtros.folhas,
    filtros.status,
    filtros.carteiras,
  ];

  const {
    data: payrolls,
    isPending: loadingOperacoes,
    refetch: reloadPayrolls,
  } = useQuery({
    queryKey: payrollsQueryKey,
    queryFn: () => searchPayrolls(filtros),
  });

  useEffect(() => {
    if (!payrolls) {
      return;
    }

    const { carteiras: carteirasSelected } = filtros;
    const filtered = payrolls.filter(({ folha }) => {
      if (folha && carteirasSelected.length > 0) {
        return carteirasSelected.includes(folha.carteiraFolha);
      }

      return true;
    });

    if (filtered.length !== pagination.totalRecords) {
      pagination.updateTotalRecords(filtered.length);
    }
    setFilteredPayrolls(filtered);
  }, [payrolls, filtros.carteiras]);

  useEffect(() => {
    if (!filteredPayrolls) {
      return;
    }

    const offset = pagination.getOffset();
    const sorted = getSortedItems(filteredPayrolls, orderBy);
    const newPayrollPage = sorted.slice(offset, offset + pagination.pageSize);
    setPayrollPage(newPayrollPage);
  }, [filteredPayrolls, pagination.pageSize, pagination.currentPage, orderBy]);

  useEffect(() => {
    if (!payrolls) {
      return;
    }

    const carteirasFiltrada = [];
    payrolls.forEach(({ folha }) => {
      if (folha && !carteirasFiltrada.includes(folha.carteiraFolha)) {
        carteirasFiltrada.push(folha.carteiraFolha);
      }
    });
    setCarteirasDisponiveis(carteirasFiltrada);
  }, [payrolls]);

  useEffect(() => {
    const { status, carteiras } = filtros;
    setStoredFilters({ status, carteiras });
  }, [filtros]);

  const handleExportWorksheetClick = async () => {
    const hide = message.loading('Seu download está sendo preparado');
    try {
      const payrollsWorksheet = await downloadPayrollsWorksheet(filtros);
      const competencia = filtros.atualizadoEmInicial.format('MM-YYYY');
      fileDownload(payrollsWorksheet, `requisicoes-${competencia}.xls`);
    } catch (e) {
      mostrarErro(e);
    } finally {
      hide();
    }
  };

  const handleTransmitToApdata = async (payroll) => {
    const notificationKey = `transmitting-notification-${payroll.id}`;
    notification.info({
      icon: <LoadingOutlined />,
      message: <span>Transmitindo a requisição {payroll.id} para o apdata. Isso pode demorar alguns segundos!</span>,
      duration: 0,
      key: notificationKey,
    });
    try {
      await transmitToApdata(payroll.id);
      await reloadPayrolls();
      return notification.success({
        message: <span>Requisição {payroll.id} transmitida com sucesso para o apdata!</span>,
        duration: 4.5,
      });
    } catch (e) {
      if (e.response && e.response.status >= 400 && e.response.status < 500) {
        return Modal.error({
          title: `Erro na transmissão da requisição ${payroll.id}`,
          content: e.response.data.message,
        });
      }
      return mostrarErro(e);
    } finally {
      notification.close(notificationKey);
    }
  };

  const handleEarlyPayment = async (payroll) => {
    setShowPaymentAdvanceModal(true);
    setPayrollSelected(payroll);
  };

  const handleFinishEditing = (requisicao, body) => {
    queryClient.setQueryData(payrollsQueryKey, (currentPayrolls) => {
      if (!Array.isArray(currentPayrolls)) {
        return currentPayrolls;
      }

      return currentPayrolls.map((payroll) => {
        if (payroll.id !== requisicao.id) {
          return payroll;
        }

        return {
          ...payroll,
          observacaoAdmin: body.observacaoAdmin,
          atualizadoEm: moment(),
        };
      });
    });
    setEditingPayroll(undefined);
  };

  const canShowApdataInfo = userAccount.ability.can('view', 'payroll.apdata-info');

  return (
    <Page
      projeto="Cooperativa"
      titulo="Gerenciamento de Requisições"
      linkManual={process.env.REACT_APP_LINK_MANUAL}
    >
      <DrawerEditPayroll
        visible={!!editingPayroll}
        payroll={editingPayroll}
        onCancelClick={() => setEditingPayroll(undefined)}
        onFinishEditing={handleFinishEditing}
      />

      <PayrollSearchFilters
        defaultFilters={defaultFilterValues}
        appliedFilters={filtros}
        onApplyFiltersClick={(updatedFilters) => setFiltros(updatedFilters)}
        onClearFiltersClick={() => setFiltros(defaultFilterValues)}
        carteiraList={carteirasDisponiveis}
        canShowApdataInfo={canShowApdataInfo}
      />

      <br />

      <Skeleton
        loading={loadingOperacoes}
        data-testid="skeleton-operacoes"
      >
        {payrolls && payrolls.length > 0 ? (
          <>
            <PayrollTable
              payrolls={payrollPage}
              onEditClick={(req) => setEditingPayroll(req)}
              onTransmitClick={(req) => handleTransmitToApdata(req)}
              onEarlyPayment={(req) => handleEarlyPayment(req)}
              onChangeOrder={setOrderBy}
              orderBy={orderBy}
              canShowApdataInfo={canShowApdataInfo}
            />
            <br />
            <Row
              gutter={[16, 16]}
              xs={24}
              sm={24}
              md={24}
              lg={24}
            >
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={9}
                xl={7}
                xxl={5}
              >
                <Pagination
                  simple
                  current={pagination.currentPage}
                  pageSize={pagination.pageSize}
                  total={pagination.totalRecords}
                  onChange={(page) => pagination.goToPage(page)}
                  onShowSizeChange={(current, size) => pagination.updatePageSize(size)}
                  pageSizeOptions={['10', '20', '30']}
                  showSizeChanger
                />
              </Col>
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={9}
                xl={7}
                xxl={5}
                flex="flex-end"
              >
                <ButtonPanel>
                  <BwgButton onClick={handleExportWorksheetClick}>Exportar Excel</BwgButton>
                </ButtonPanel>
              </Col>
            </Row>
          </>
        ) : (
          <EmptyWrapper>
            <Empty description="Nenhuma requisição encontrada" />
          </EmptyWrapper>
        )}
      </Skeleton>
      <ModalAnteciparRepasse
        payroll={payrollSelected}
        onCloseModal={() => setShowPaymentAdvanceModal(false)}
        onPaymentAdvanceComplete={async () => {
          await reloadPayrolls();
          setShowPaymentAdvanceModal(false);
        }}
        open={showPaymentAdvanceModal}
      />
    </Page>
  );
}
